import { useState, useMemo } from 'react';
import { SlidersHorizontal, RefreshCw } from 'lucide-react';
import { CountryAutocomplete } from './CountryAutocomplete';
import type { SimRule } from '../../lib/rule-match';
import { simulate, getUniqueCountries, getUniqueDeviceTypes, getUniqueOSes } from '../../lib/rule-match';
import type { OfferData } from './SegmentCard';

export function RuleSimulator({
  rules,
  offers,
}: {
  rules: SimRule[];
  offers: OfferData[];
}) {
  const [country, setCountry] = useState('*');
  const [deviceType, setDeviceType] = useState('*');
  const [os, setOs] = useState('*');
  const [source, setSource] = useState('');
  const [results, setResults] = useState<{ winner: SimRule; pool: SimRule[] } | null>(null);

  const uniqueCountries = useMemo(() => getUniqueCountries(rules), [rules]);
  const uniqueDevices = useMemo(() => getUniqueDeviceTypes(rules), [rules]);
  const uniqueOSesList = useMemo(() => getUniqueOSes(rules), [rules]);

  const handleSimulate = () => {
    const result = simulate(rules, {
      country: country.toUpperCase(),
      deviceType: deviceType.toLowerCase(),
      os: os.toLowerCase(),
      source: source.trim() || '*',
    });
    setResults(result);
  };

  const handleReset = () => {
    setCountry('*');
    setDeviceType('*');
    setOs('*');
    setSource('');
    setResults(null);
  };

  return (
    <details className="cyber-panel">
      <summary className="px-3 py-2 cursor-pointer text-[10px] font-display uppercase tracking-wider text-text-secondary hover:text-text-primary flex items-center justify-between">
        <span className="flex items-center gap-2">
          <SlidersHorizontal size={12} />
          Rule Simulator
        </span>
        <span className="text-[9px] font-mono text-text-tertiary">Test routing logic</span>
      </summary>
      <div className="px-3 py-4 space-y-3">
        {/* Form */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1">
            <label className="text-[10px] font-mono text-text-tertiary uppercase block mb-1">
              Country
            </label>
            <CountryAutocomplete
              value={country === '*' ? '' : country}
              onChange={(v) => setCountry(v)}
              onSelect={(c) => setCountry(c)}
              onBackspaceEmpty={() => setCountry('*')}
              placeholder="US, ID, *"
            />
          </div>

          <div className="col-span-1">
            <label className="text-[10px] font-mono text-text-tertiary uppercase block mb-1">
              Device
            </label>
            <select
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              className="input-cyber h-8 text-xs w-full"
            >
              {uniqueDevices.map((d: string) => (
                <option key={d} value={d}>
                  {d === '*' ? 'Any device' : d}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-1">
            <label className="text-[10px] font-mono text-text-tertiary uppercase block mb-1">
              OS
            </label>
            <select
              value={os}
              onChange={(e) => setOs(e.target.value)}
              className="input-cyber h-8 text-xs w-full"
            >
              {uniqueOSesList.map((o: string) => (
                <option key={o} value={o}>
                  {o === '*' ? 'Any OS' : o}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-1">
            <label className="text-[10px] font-mono text-text-tertiary uppercase block mb-1">
              Source (optional)
            </label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="input-cyber h-8 text-xs w-full"
              placeholder="google, fb, *..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleSimulate}
            className="text-[10px] font-display uppercase tracking-wider px-3 py-1.5 rounded border border-neon-cyan/40 bg-neon-cyan/5 text-neon-cyan hover:bg-neon-cyan/10 flex items-center gap-1"
          >
            <RefreshCw size={12} />
            Simulate
          </button>
          <button
            onClick={handleReset}
            className="text-[10px] font-display uppercase tracking-wider px-3 py-1.5 rounded border border-border-subtle text-text-tertiary hover:text-text-secondary"
          >
            Reset
          </button>
        </div>

        {/* Results */}
        {results != null && (
          <div className="space-y-2 pt-2 border-t border-border-subtle">
            {results.winner ? (
              <>
                <div className="p-3 bg-neon-magenta/5 border border-neon-magenta/30 rounded">
                  <div className="text-[10px] font-display uppercase tracking-widest text-neon-magenta mb-1">
                    Winner Segment
                  </div>
                  <div className="text-xs font-mono text-text-primary mb-2">
                    {offerName(results.winner.offerId, offers)} • weight {results.winner.weight}%
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {formatRuleRules(results.winner).map((spec, i) => (
                      <span
                        key={i}
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-neon-magenta/10 text-neon-magenta border border-neon-magenta/30"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-3 bg-bg-elevated border border-border-subtle rounded">
                  <div className="text-[10px] font-display uppercase tracking-widest text-text-secondary mb-2">
                    Pool Distribution ({results.pool.length} rule(s))
                  </div>
                  <ul className="space-y-1.5">
                    {results.pool.map((r, i) => {
                      const pct = Math.round((r.weight / results.pool.reduce((s, x) => s + x.weight, 0)) * 100);
                      return (
                        <li key={r.id} className="flex items-center justify-between text-xs font-mono">
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-sm ${
                                i === 0 ? 'bg-neon-magenta' : 'bg-neon-cyan'
                              }`}
                            />
                            <span className="text-text-primary">{offerName(r.offerId, offers)}</span>
                            <span className="text-text-tertiary">weight {r.weight}</span>
                          </div>
                          <span className="text-text-secondary">%{pct.toFixed(0)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            ) : (
              <div className="p-3 bg-bg-tertiary border border-border-subtle rounded text-center">
                <div className="text-[10px] font-mono text-text-tertiary">No matching segment found</div>
                <div className="text-[9px] font-mono text-text-quaternary mt-0.5">
                  Traffic will fall back to fallback offer (or 404)
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function offerName(offerId: number, offers: OfferData[]): string {
  return offers.find((o) => o.id === offerId)?.name ?? `Offer #${offerId}`;
}

function formatRuleRules(rule: SimRule): string[] {
  const parts: string[] = [];
  if (rule.country && rule.country !== '*') parts.push(`country=${rule.country.toUpperCase()}`);
  if (rule.deviceType && rule.deviceType !== '*') parts.push(`device=${rule.deviceType}`);
  if (rule.os && rule.os !== '*') parts.push(`os=${rule.os}`);
  if (rule.source && rule.source !== '*') parts.push(`source=${rule.source}`);
  return parts.length ? parts : ['Catch-all'];
}
